/**
 * POS Settings Page
 */

import React, { useState } from 'react';
import { Printer, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { PwaAccountCard } from '@/components/pwa/PwaAccountGate';
import { useAuth } from '../context/AuthContext';
import { getTerminalId, setTerminalId } from '../services/displayService';
import { pwaUrl } from '@/utils/getBasePath';
import { copyText } from '@/lib/copyText';

// A display channel: letters, digits, - and _ (it is part of the display's link)
const cleanChannel = (v) => String(v || '').toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 16);

/**
 * Customer display: each register pushes its checkout to ONE display channel. The first
 * register uses T001 (what Back Office -> Customer Display links to); a second register
 * picks T002, and its display opens the link shown here.
 */
function CustomerDisplayChannel() {
  const [channel, setChannel] = useState(() => getTerminalId());
  const [saved, setSaved] = useState(() => getTerminalId());
  const link = `${pwaUrl('display')}/t/${encodeURIComponent(saved)}`;
  const save = () => {
    const next = cleanChannel(channel) || 'T001';
    setTerminalId(next);
    setChannel(next);
    setSaved(next);
    toast.success(`This register now shows its checkout on display ${next}`);
  };
  return (
    <div className="bg-slate-800 rounded-xl p-6 mb-6" data-testid="pos-display-channel">
      <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
        <Monitor className="w-5 h-5 text-emerald-400" />
        Customer display
      </h2>
      <p className="text-sm text-slate-400 mb-4">
        Each register needs its own channel. The first register uses T001; give a second register T002, and so on.
      </p>
      <div className="flex gap-2 items-end">
        <div>
          <label className="text-sm text-slate-400 mb-2 block" htmlFor="pos-display-channel-input">This register's channel</label>
          <Input
            id="pos-display-channel-input"
            data-testid="pos-display-channel-input"
            value={channel}
            onChange={(e) => setChannel(cleanChannel(e.target.value))}
            className="bg-slate-700 border-slate-600 text-white w-40"
          />
        </div>
        <Button type="button" data-testid="pos-display-channel-save" onClick={save} disabled={!cleanChannel(channel) || cleanChannel(channel) === saved}
          className="bg-emerald-600 hover:bg-emerald-700">
          Use this channel
        </Button>
      </div>
      <div className="mt-4">
        <p className="text-sm text-slate-400 mb-1">Open this link on the customer display for this register:</p>
        <div className="flex gap-2 items-center">
          <code className="text-emerald-300 text-sm break-all" data-testid="pos-display-link">{link}</code>
          <Button type="button" variant="outline" size="sm" onClick={() => copyText(link, 'Display link copied')}
            className="border-slate-600 text-white bg-transparent hover:bg-slate-700">
            Copy
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { logout } = useAuth();

  return (
    <div className="h-screen bg-slate-950 p-6 overflow-y-auto">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold text-white mb-6">POS Settings</h1>

        {/* Printers are the restaurant's, set up once in the back office (kitchen-service) */}
        <div className="bg-slate-800 rounded-xl p-6 mb-6" data-testid="pos-printing-info">
          <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
            <Printer className="w-5 h-5 text-emerald-400" />
            Printing
          </h2>
          <p className="text-sm text-slate-400">
            Kitchen and receipt printers, and which tickets go to which printer, are set up for the
            whole restaurant in Back Office &rarr; Kitchen Printers.
          </p>
        </div>

        <CustomerDisplayChannel />

        {/* Restaurant account: signing it out also signs the cashier out */}
        <div className="mb-6">
          <PwaAccountCard onSignOut={logout}
            note="Signing out the restaurant account also signs out the cashier; the next person needs the account sign-in, then their phone + PIN." />
        </div>
      </div>
    </div>
  );
}
